/**
 * AnalysisLayout Component
 * 
 * Reusable layout wrapper for all analysis sections.
 * Provides consistent structure with title, description, and scrollable content area.
 */

// 1. External dependencies
import React from 'react';
import { BarChart3 } from 'lucide-react';

// 2. Internal absolute imports
import { Card, CardHeader, CardBody, ScrollShadow, Button, ScrollShadowRef } from '@enfomentis/ui';

export interface AnalysisLayoutProps {
  /** Section title */
  title: string;
  /** Section description */
  description: string;
  /** Analysis content */
  children: React.ReactNode;
  /** Function to open the analysis drawer */
  onOpenDrawer?: () => void;
  /** Ref for scroll container */
  scrollRef?: React.RefObject<ScrollShadowRef>;
}

/**
 * Reusable Analysis Section Layout
 * 
 * Provides consistent Card-based structure for all analysis sections:
 * - Card Header with section title and description
 * - Card Body with scrollable content area
 * - Proper mobile behavior and spacing
 */
export const AnalysisLayout: React.FC<AnalysisLayoutProps> = ({
  title,
  description,
  children,
  onOpenDrawer,
  scrollRef
}) => {
  return (
    <Card 
      radius="none" 
      shadow="none"
      className="h-full border-0 relative"
    >
      {/* Floating Right Sidebar Button */}
      {onOpenDrawer && (
        <div className="fixed right-0 top-22 z-40">
          <Button
            variant="faded"
            color="secondary"
            size="sm"
            isIconOnly
            onPress={onOpenDrawer}
            title="Open Analysis Menu"
            className="rounded-l-full rounded-r-none"
          >
            <BarChart3 size={19} />
          </Button>
        </div>
      )}
      
      <CardHeader className="flex items-start justify-between pb-4">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-text-primary">
            {title}
          </h2>
          <p className="text-text-secondary mt-1">
            {description}
          </p>
        </div>
      </CardHeader>
      <CardBody className="flex-1 min-h-0 p-0">
        <ScrollShadow 
          ref={scrollRef}
          className="h-full p-6"
          hideScrollBar
          size={20}
        >
          {children}
        </ScrollShadow>
      </CardBody>
    </Card>
  );
};